import { useRef, useState } from "react";
import { ArrowLeft, LockKeyhole, Mail, ShieldCheck } from "lucide-react";
import { AuthField, AuthSplitLayout } from "@/components/AuthSplitLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function LoginPage() {
  const { login, verifyTwoFactor, sessionNotice } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [trustedDevice, setTrustedDevice] = useState(false);
  const [trustedEligible, setTrustedEligible] = useState(false);
  const [deviceAlreadyTrusted, setDeviceAlreadyTrusted] = useState(false);
  const [challengeToken, setChallengeToken] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(sessionNotice || "");
  const [providerHint, setProviderHint] = useState(false);
  const [clientHint, setClientHint] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const emailRef = useRef(null);
  const isReset = mode === "reset";
  const isTwoFactor = mode === "2fa";
  const submitLabel = isSubmitting ? "Please wait…" : isReset ? "Send reset instructions" : isTwoFactor ? "Verify code" : "Sign in";

  async function handleLogin(event) {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      const result = await login({ email, password, remember_me: rememberMe });
      if (result.twoFactorRequired) {
        setChallengeToken(result.challengeToken);
        setTrustedEligible(result.trustedDeviceEligible);
        setDeviceAlreadyTrusted(result.deviceAlreadyTrusted);
        setTrustedDevice(result.deviceAlreadyTrusted);
        setNotice("");
        setMode("2fa");
      }
    } catch (loginError) {
      setError(loginError.message);
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleTwoFactor(event) {
    event.preventDefault();
    setError("");
    setIsSubmitting(true);
    try {
      await verifyTwoFactor(challengeToken, code, trustedEligible && trustedDevice);
    } catch (verifyError) {
      setError(verifyError.message);
      if (/expired|start again/i.test(verifyError.message)) {
        setMode("login");
        setCode("");
      }
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleReset(event) {
    event.preventDefault();
    setError("");
    setNotice("");
    setIsSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setNotice("If an account matches this email, a reset link has been emailed to it.");
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setIsSubmitting(false);
    }
  }

  function switchMode(nextMode) {
    setMode(nextMode);
    setError("");
    setNotice("");
  }

  return (
    <AuthSplitLayout
      testId="login-page"
      statement="Built to run the operation."
      support="From staffing request to invoice, TAGForce runs the entire operation."
      features={[
        "Placements and provider assignments",
        "Evaluations and compliance tracking",
        "Approvals, reporting, and invoicing",
      ]}
      heading={isReset ? "Reset your password" : isTwoFactor ? "Enter your verification code" : "Welcome back"}
      description={isReset ? "Enter your email and we will send reset instructions." : isTwoFactor ? "Open your authenticator app and enter the 6-digit code, or use one of your recovery codes." : "Sign in to your TAGForce workspace."}
      footer={
        isReset || isTwoFactor ? (
          <button
            data-testid="login-return-button"
            className="login-link-button login-return"
            type="button"
            onClick={() => { switchMode("login"); setCode(""); }}
          >
            <ArrowLeft size={14} /> Return to sign in
          </button>
        ) : (
          <>
            <hr className="login-divider" />
            <button
              className="login-link-button login-provider-link"
              data-testid="provider-signin-link"
              type="button"
              onClick={() => {
                setProviderHint(true);
                emailRef.current?.focus();
              }}
            >
              Provider? Sign in with your provider account
            </button>
            {providerHint && (
              <p className="login-provider-hint" data-testid="provider-signin-hint">
                Providers use the same form. Enter the email and password TAG issued for your Provider Portal — it opens
                automatically after you sign in.
              </p>
            )}
            <p className="login-client-help" data-testid="client-signin-help">
              School or partner? Sign in with the email your invitation was sent to.
              <button
                className="login-link-button login-client-help-link"
                data-testid="client-signin-first-time"
                type="button"
                onClick={() => setClientHint((value) => !value)}
              >
                First time?
              </button>
            </p>
            {clientHint && (
              <p className="login-provider-hint" data-testid="client-signin-hint">
                First time? Use the link in your invitation email to set your password. If the link has expired, ask TAG to resend it.
              </p>
            )}
          </>
        )
      }
    >
      <form onSubmit={isReset ? handleReset : isTwoFactor ? handleTwoFactor : handleLogin} className="login-form" data-testid="login-form">
        {isTwoFactor ? (
          <AuthField id="two-factor-code" label="Authenticator or recovery code" icon={ShieldCheck}>
            <input
              data-testid="login-2fa-code-input"
              id="two-factor-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="123 456"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              required
              autoFocus
            />
          </AuthField>
        ) : (
        <AuthField id="email" label="Email" icon={Mail}>
          <input
            data-testid="login-email-input"
            id="email"
            ref={emailRef}
            type="email"
            placeholder="you@yourorganization.org"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
          />
        </AuthField>
        )}

        {!isReset && !isTwoFactor && (
          <>
            <AuthField
              id="password"
              label="Password"
              icon={LockKeyhole}
              action={
                <button
                  data-testid="login-toggle-reset-button"
                  className="login-link-button"
                  type="button"
                  onClick={() => switchMode("reset")}
                >
                  Forgot password?
                </button>
              }
            >
              <input
                data-testid="login-password-input"
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
                autoComplete="current-password"
              />
            </AuthField>
            <label className="login-remember" htmlFor="remember-me">
              <input
                data-testid="login-remember-me"
                id="remember-me"
                type="checkbox"
                checked={rememberMe}
                onChange={(event) => setRememberMe(event.target.checked)}
              />
              <span>Keep me signed in on this device</span>
            </label>
          </>
        )}

        {isTwoFactor && trustedEligible && (
          <label className="login-remember login-trusted-device" htmlFor="trusted-device" data-testid="login-trusted-device-option">
            <input
              data-testid="login-trusted-device"
              id="trusted-device"
              type="checkbox"
              checked={trustedDevice}
              onChange={(event) => setTrustedDevice(event.target.checked)}
            />
            <span>
              <strong>Trusted device</strong> (System Administrator) — {deviceAlreadyTrusted ? "this device is already trusted; " : ""}idle timeout extends to 4 hours after this two-factor sign-in. The 12-hour session limit still applies.
            </span>
          </label>
        )}

        {error && (
          <p className="form-message error-message" data-testid="login-error-message">{error}</p>
        )}
        {notice && (
          <p className="form-message success-message" data-testid="login-success-message">{notice}</p>
        )}

        <button data-testid="login-submit-button" className="login-submit" type="submit" disabled={isSubmitting}>
          {submitLabel}
        </button>
      </form>
    </AuthSplitLayout>
  );
}
